import React from 'react';
import { Button, Empty, Modal, Typography } from 'antd';
import { RichTextViewer } from '../../components/RichTextEditor';
import { authorName } from './authors';
import { formatLogged } from './entryTable';
import IssueListHeadMeta from './IssueListHeadMeta';
import { useGetProgramQuery } from '../../redux/api/manufacturing';
import type { ProgramReview } from '../../interfaces/CamProgramming.interface';

const { Text } = Typography;

interface ReviewIssuesModalProps {
	programId: string;
	review: ProgramReview | null;
	open: boolean;
	onClose: () => void;
	afterClose?: () => void;
}

/**
 * The issues one review raised, read-only.
 *
 * The table layout flattens reviews and issues into separate tabs, which loses
 * the link between them. Reaching them from the review's own issue count puts
 * it back without giving the row a nested list to carry.
 */
const ReviewIssuesModal: React.FC<ReviewIssuesModalProps> = ({
	programId,
	review,
	open,
	onClose,
	afterClose,
}) => {
	const { data: program } = useGetProgramQuery(programId, {
		skip: !programId || !open,
	});
	const issues = review?.issues || [];

	return (
		<Modal
			className="cam-modal"
			title={`Issues from ${authorName(review?.reviewer ?? null, 'this review')}`}
			open={open}
			centered
			onCancel={onClose}
			afterClose={afterClose}
			footer={<Button onClick={onClose}>Close</Button>}
			width={780}
			destroyOnClose
		>
			{issues.length === 0 ? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description="No issues on this review"
				/>
			) : (
				<div className="cam-thread__issues">
					{issues.map((issue) => (
						<div
							key={issue.id}
							className={`cam-issue cam-issue--${issue.severity}`}
						>
							<div className="cam-issue__head">
								<span className="cam-issue__severity">
									{issue.severity_display}
								</span>
								<IssueListHeadMeta issue={issue} program={program} />
								<Text type="secondary" className="cam-issue__by" ellipsis>
									Reported by {authorName(issue.reported_by, 'Unknown')}
								</Text>
								<Text type="secondary" className="cam-thread__time">
									{formatLogged(issue.created_time)}
								</Text>
							</div>
							<RichTextViewer
								html={issue.description}
								emptyText="No description"
								className="cam-entry__body"
							/>
						</div>
					))}
				</div>
			)}
		</Modal>
	);
};

export default ReviewIssuesModal;
